import type * as Api from '@ViewModels';

export type BehaviorSwitchCaseLike = Api.IAutomationStepCaseStatement & {
	_type?: string;
	caseType?: string;
	isTimeout?: boolean;
};

export function isBehaviorSwitchStepType(stepType: unknown): stepType is 'BehaviorSwitchAutomationStep' {
	return stepType === 'BehaviorSwitchAutomationStep';
}

export function doesAutomationStepRequireSchedule(stepType: unknown): boolean {
	return stepType !== 'ActionItemAutomationStep' && !isBehaviorSwitchStepType(stepType);
}

function parseTimeSpanToMilliseconds(value: unknown): number | null {
	if (typeof value === 'number' && Number.isFinite(value)) {
		return value > 0 ? value : null;
	}

	if (typeof value !== 'string') {
		return null;
	}

	const trimmed = value.trim();
	if (!trimmed) {
		return null;
	}

	const dayTimeMatch = trimmed.match(/^(-?\d+)\.(\d{1,2}):(\d{2}):(\d{2})$/);
	if (dayTimeMatch) {
		const [, daysText, hoursText, minutesText, secondsText] = dayTimeMatch;
		const totalMilliseconds =
			((Number(daysText) * 24 + Number(hoursText)) * 60 * 60 + Number(minutesText) * 60 + Number(secondsText)) * 1000;
		return Number.isFinite(totalMilliseconds) && totalMilliseconds > 0 ? totalMilliseconds : null;
	}

	const timeMatch = trimmed.match(/^(-?\d+):(\d{2}):(\d{2})$/);
	if (timeMatch) {
		const [, hoursText, minutesText, secondsText] = timeMatch;
		const totalMilliseconds = (Number(hoursText) * 60 * 60 + Number(minutesText) * 60 + Number(secondsText)) * 1000;
		return Number.isFinite(totalMilliseconds) && totalMilliseconds > 0 ? totalMilliseconds : null;
	}

	return null;
}

export function hasPositiveTimeSpan(value: unknown): boolean {
	return parseTimeSpanToMilliseconds(value) !== null;
}

export function isBehaviorTimeoutCase(caseStatement: BehaviorSwitchCaseLike): boolean {
	const caseType = (caseStatement.caseType || caseStatement._type || '').toLowerCase();
	return (
		caseStatement.isDefault === true ||
		caseStatement.isTimeout === true ||
		caseType.includes('timeoutcasestatement') ||
		caseType.includes('defaultcasestatement')
	);
}

export function getBehaviorSwitchCaseCounts(cases: BehaviorSwitchCaseLike[] | null | undefined): {
	totalCases: number;
	timeoutCaseCount: number;
	behaviorCaseCount: number;
} {
	const normalizedCases = cases || [];
	const timeoutCaseCount = normalizedCases.filter(isBehaviorTimeoutCase).length;

	return {
		totalCases: normalizedCases.length,
		timeoutCaseCount,
		behaviorCaseCount: normalizedCases.length - timeoutCaseCount,
	};
}
